import React, { useCallback, useState } from 'react';

import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { Chip } from '@/components/shared/Chip';
import {
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import AsaphDialog from '@/components/shared/Dialog/AsaphDialog';
import {
	SettingSectionTitle,
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { UpgradeGateSheet } from '@/components/shared/UpgradeGateSheet';
import { UserAvatar } from '@/components/UserAvatar';
import { UserTopSongs } from '@/components/UserTopSongs';
import { LinkFrom, PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { directMessageUrl } from '@/config/urls';
import {
	useCreateChatMutation,
	useDemoteUserToTeamMemberMutation,
	usePromoteUserToTeamAdminMutation,
	useRemoveUserFromTeamMutation,
} from '@/graphql';
import { sentenceCase } from '@/helpers/changeCase';
import { useAuth } from '@/providers/AuthProvider';
import { TeamMembership, useTeam } from '@/providers/TeamProvider';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useCopyToClipboard } from 'react-use';

import styles from './ProfileModal.module.scss';

interface ProfileModalProps {
	memberId: string;
	open: boolean;
	onClose: () => void;
	onRemoveFromGroup?: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
	memberId,
	open,
	onClose,
	onRemoveFromGroup,
}) => {
	const { team, refetch } = useTeam();
	const { user } = useAuth();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const [, copyToClipboard] = useCopyToClipboard();

	const [makeAdminSheetOpen, setMakeAdminSheetOpen] = useState(false);
	const [removeAdminSheetOpen, setRemoveAdminSheetOpen] = useState(false);
	const [showAdminsGate, setShowAdminsGate] = useState(false);

	const adminCount = (team.members ?? []).filter((m) =>
		m?.roles.includes('teamAdmin'),
	).length;
	const [removeFromTeamSheetOpen, setRemoveFromTeamSheetOpen] =
		useState(false);
	const [removeFromGroupSheetOpen, setRemoveFromGroupSheetOpen] =
		useState(false);

	const { mutateAsync: createChatMutate } = useCreateChatMutation();
	const { mutateAsync: promoteUserMutate } =
		usePromoteUserToTeamAdminMutation();
	const { mutateAsync: demoteUserMutate } =
		useDemoteUserToTeamMemberMutation();
	const { mutateAsync: removeUserMutate } = useRemoveUserFromTeamMutation();

	const member: TeamMembership | undefined = team.members.find(
		(m) => m.user.id === memberId,
	);

	const isSelf = user.id === memberId;
	const isViewerAdmin =
		team.members
			.find((m) => m.user.id === user.id)
			?.roles.includes('teamAdmin') ?? false;
	const isViewedMemberAdmin = member?.roles.includes('teamAdmin') ?? false;

	const handleMessage = useCallback(async () => {
		try {
			const result = await createChatMutate({
				userIds: [user.id, memberId],
			});
			navigate(directMessageUrl.replace(':token', result.upsertChat.id), {
				state: { linkFrom: LinkFrom.messages },
			});
			onClose();
		} catch {
			enqueueSnackbar('Something went wrong');
		}
	}, [
		createChatMutate,
		user.id,
		memberId,
		navigate,
		onClose,
		enqueueSnackbar,
	]);

	const handleMakeAdmin = useCallback(() => {
		if (adminCount >= 1) {
			setShowAdminsGate(true);
			return;
		}
		setMakeAdminSheetOpen(true);
	}, [adminCount]);

	const handleConfirmMakeAdmin = useCallback(async () => {
		setMakeAdminSheetOpen(false);
		await promoteUserMutate({ userId: memberId });
		refetch();
	}, [memberId, promoteUserMutate, refetch]);

	const handleRemoveAdmin = useCallback(() => {
		setRemoveAdminSheetOpen(true);
	}, []);

	const handleConfirmRemoveAdmin = useCallback(async () => {
		setRemoveAdminSheetOpen(false);
		await demoteUserMutate({ userId: memberId });
		refetch();
	}, [memberId, demoteUserMutate, refetch]);

	const handleRemove = useCallback(() => {
		setRemoveFromTeamSheetOpen(true);
	}, []);

	const handleConfirmRemove = useCallback(async () => {
		setRemoveFromTeamSheetOpen(false);
		await removeUserMutate({ userId: memberId });
		onClose();
	}, [memberId, removeUserMutate, onClose]);

	const handleRemoveFromGroup = useCallback(() => {
		setRemoveFromGroupSheetOpen(true);
	}, []);

	const handleConfirmRemoveFromGroup = useCallback(async () => {
		setRemoveFromGroupSheetOpen(false);
		onRemoveFromGroup?.();
	}, [onRemoveFromGroup]);

	if (!member) {
		return null;
	}

	const firstName = (member.user.displayName ?? 'Member').split(' ')[0];

	const mainItems: SettingsListItems = isSelf
		? []
		: [
				{
					type: 'setting' as const,
					title: `Message ${firstName}`,
					onClick: handleMessage,
				},
				...(isViewerAdmin
					? [
							{
								type: 'setting' as const,
								title: 'Email',
								trailingText: member.user.email,
								onClick: () => {
									copyToClipboard(member.user.email ?? '');
									enqueueSnackbar('Email copied');
								},
								noChevron: true,
							},
					  ]
					: []),
		  ];

	const manageItems: SettingsListItems = isSelf
		? []
		: [
				...(isViewerAdmin && !isViewedMemberAdmin
					? [
							{
								type: 'setting' as const,
								title: 'Make admin',
								onClick: handleMakeAdmin,
							},
					  ]
					: []),
				...(isViewerAdmin && isViewedMemberAdmin
					? [
							{
								type: 'setting' as const,
								title: 'Remove admin',
								onClick: handleRemoveAdmin,
							},
					  ]
					: []),
				...(onRemoveFromGroup
					? [
							{
								type: 'setting' as const,
								title: 'Remove from group',
								red: true,
								onClick: handleRemoveFromGroup,
							},
					  ]
					: []),
				...(isViewerAdmin && !onRemoveFromGroup
					? [
							{
								type: 'setting' as const,
								title: 'Remove from team',
								red: true,
								onClick: handleRemove,
							},
					  ]
					: []),
		  ];

	return (
		<>
			<AsaphDialog open={open} onClose={onClose}>
				<div className={styles['profile-modal__toolbar']}>
					<AsaphDialogHeader
						leadingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component: (
								<AsaphDialogHeaderButton
									type="text"
									label="Done"
									handlePress={onClose}
								/>
							),
						}}
						title={{
							needsFadeIn: false,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							label: '',
						}}
						trailingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component: isViewedMemberAdmin ? (
								<Chip variant="rounded" color="light-gray">
									Admin
								</Chip>
							) : null,
						}}
					/>
				</div>
				<section className={styles['profile-modal__content']}>
					<div className={styles['profile-modal__identity']}>
						<div className={styles['profile-modal__avatar']}>
							<UserAvatar
								size="x-large"
								userId={memberId}
								activeSize="medium"
							/>
						</div>
						<div className={styles['profile-modal__name']}>
							{member.user.displayName}
						</div>
						{member.user.skills.length > 0 && (
							<div className={styles['profile-modal__roles']}>
								{member.user.skills
									.map((s) => sentenceCase(s))
									.join(', ')}
							</div>
						)}
					</div>
					<div className="vsb-20" />
					{mainItems.length > 0 && (
						<SettingsList items={mainItems} noChevron />
					)}
					<UserTopSongs userId={memberId} variant="profile" />
					{manageItems.length > 0 && (
						<>
							<SettingSectionTitle
								type="title"
								title={`Manage ${firstName}`}
							/>
							<SettingsList items={manageItems} noChevron />
						</>
					)}
				</section>
			</AsaphDialog>
			<UpgradeGateSheet
				open={showAdminsGate}
				onClose={() => setShowAdminsGate(false)}
				heading="Unlimited admins"
				body="Unlimited admins are available on the Team plan. Continue to promote this member anyway."
				gateName="admins"
				onSkip={() => setMakeAdminSheetOpen(true)}
				rememberAcceptance={true}
			/>
			<ActionSheet
				open={makeAdminSheetOpen}
				title={
					<span>
						Make <strong>{member.user.displayName}</strong> an
						admin?
					</span>
				}
				onClose={() => setMakeAdminSheetOpen(false)}
			>
				<ActionSheetAction
					label="Make admin"
					onActionClick={handleConfirmMakeAdmin}
				/>
			</ActionSheet>
			<ActionSheet
				open={removeAdminSheetOpen}
				title={
					<span>
						Remove <strong>{member.user.displayName}</strong> as
						admin?
					</span>
				}
				onClose={() => setRemoveAdminSheetOpen(false)}
			>
				<ActionSheetAction
					label="Remove admin"
					onActionClick={handleConfirmRemoveAdmin}
					type="danger"
				/>
			</ActionSheet>
			<ActionSheet
				open={removeFromTeamSheetOpen}
				title={
					<span>
						Remove <strong>{member.user.displayName}</strong> from
						the team?
					</span>
				}
				onClose={() => setRemoveFromTeamSheetOpen(false)}
			>
				<ActionSheetAction
					label="Remove from team"
					onActionClick={handleConfirmRemove}
					type="danger"
				/>
			</ActionSheet>
			<ActionSheet
				open={removeFromGroupSheetOpen}
				title={
					<span>
						Remove <strong>{member.user.displayName}</strong> from
						this group?
					</span>
				}
				onClose={() => setRemoveFromGroupSheetOpen(false)}
			>
				<ActionSheetAction
					label="Remove from group"
					onActionClick={handleConfirmRemoveFromGroup}
					type="danger"
				/>
			</ActionSheet>
		</>
	);
};
